import React from "react";
import cx from "classnames";
import PropTypes from "prop-types";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";

// core components
import Badge from "components/Badge/Badge.js";

import styles from "assets/jss/material-dashboard-pro-react/components/timelineStyle.js";

const useStyles = makeStyles((theme) => ({
  ...styles(theme),
  // Override timeline styles for initiatives view
  timeline: {
    ...styles(theme).timeline,
    marginTop: "0", // Remove default margin
    padding: "5px 0", // Reduce padding further
    maxHeight: "none", // Remove maxHeight constraint
    overflow: "visible", // Allow proper scrolling
    height: "auto", // Let it size naturally
    marginLeft: "0", // Fix left dislocation
    paddingLeft: "0", // Remove left padding
  },
  timelineSimple: {
    ...styles(theme).timelineSimple,
    marginTop: "0", // Remove default margin
    padding: "2px 0", // Reduce padding even more
    marginLeft: "10px", // Fix left dislocation
    paddingLeft: "0", // Remove left padding
    "& > li": {
      marginBottom: "12px", // Reduce spacing between items further
      marginLeft: "0", // Ensure no left margin on list items
    },
  },
  item: {
    ...styles(theme).item,
    marginBottom: "12px", // Reduce spacing
    marginLeft: "0", // Fix left dislocation
  },
  // Override any positioning that might interfere
  timelinePanel: {
    ...styles(theme).timelinePanel,
    position: "relative", // Ensure proper positioning
    padding: "8px 12px", // Reduce padding from default
    marginLeft: "0", // Fix left dislocation
  },
  timelineSimplePanel: {
    ...styles(theme).timelineSimplePanel,
    position: "relative", // Ensure proper positioning
    padding: "6px 10px", // Reduce padding from default
    marginLeft: "0", // Fix left dislocation
  },
  // Reduce circle size around icons by additional 20% (total 30% from original)
  timelineBadge: {
    ...styles(theme).timelineBadge,
    width: "36px", // Reduce from 45px (additional 20% reduction)
    height: "36px", // Reduce from 45px (additional 20% reduction)
    lineHeight: "36px", // Match height for perfect centering
    fontSize: "1.12em", // Reduce from 1.26em (additional 20% reduction)
    display: "flex", // Use flexbox for perfect centering
    alignItems: "center", // Center vertically
    justifyContent: "center", // Center horizontally
    marginLeft: "-17px", // Fix left dislocation
    top: "23px",
  },
  timelineSimpleBadge: {
    ...styles(theme).timelineSimpleBadge,
    width: "36px", // Reduce from 45px (additional 20% reduction)
    height: "36px", // Reduce from 45px (additional 20% reduction)
    lineHeight: "36px", // Match height for perfect centering
    fontSize: "1.12em", // Reduce from 1.26em (additional 20% reduction)
    display: "flex", // Use flexbox for perfect centering
    alignItems: "center", // Center vertically
    justifyContent: "center", // Center horizontally
  },
  // Reduce icon size proportionally
  badgeIcon: {
    ...styles(theme).badgeIcon,
    fontSize: "1.12em", // Reduce from 1.26em (additional 20% reduction)
    margin: "0", // Remove any default margins
    display: "block", // Ensure proper display
  },
  // Traffic light circle styles
  trafficLightCircle: {
    position: "absolute",
    top: "8px", // Align with the badge
    right: "8px",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    zIndex: 2,
  },
  trafficLightGreen: {
    backgroundColor: "#4caf50", // Green color
  },
  trafficLightYellow: {
    backgroundColor: "#ff9800", // Yellow/Orange color
  },
  trafficLightRed: {
    backgroundColor: "#f44336", // Red color
  },
  // Status text styles
  statusText: {
    position: "absolute",
    top: "6px", // Align with the traffic light circle
    right: "34px", // Position to the left of the traffic light circle
    fontSize: "11px",
    color: "#bbbbbb", // Light gray color
    fontWeight: "normal",
    zIndex: 2,
  },
}));

export default function InitiativesTimeline(props) {
  const classes = useStyles();
  const { stories, simple } = props;
  const timelineClass =
    classes.timeline +
    " " +
    cx({
      [classes.timelineSimple]: simple,
    });
  return (
    <ul className={timelineClass}>
      {stories.map((prop, key) => {
        const panelClasses =
          classes.timelinePanel +
          " " +
          cx({
            [classes.timelinePanelInverted]: prop.inverted || simple,
            [classes.timelineSimplePanel]: simple,
          });
        const timelineBadgeClasses =
          classes.timelineBadge +
          " " +
          classes[prop.badgeColor] +
          " " +
          cx({
            [classes.timelineSimpleBadge]: simple,
          });
        return (
          <li className={classes.item} key={key}>
            {prop.badgeIcon ? (
              <div className={timelineBadgeClasses}>
                <prop.badgeIcon className={classes.badgeIcon} />
              </div>
            ) : null}
            <div className={panelClasses}>
              {/* Traffic light circle */}
              {prop.trafficLight > 0 && (
                <>
                  <div className={classes.statusText}>status</div>
                  <div
                    className={cx(classes.trafficLightCircle, {
                      [classes.trafficLightGreen]: prop.trafficLight === 1,
                      [classes.trafficLightYellow]: prop.trafficLight === 2,
                      [classes.trafficLightRed]: prop.trafficLight === 3,
                    })}
                  />
                </>
              )}
              {prop.title ? (
                <div className={classes.timelineHeading}>
                  <Badge color={prop.titleColor}>{prop.title}</Badge>
                </div>
              ) : null}
              <div className={classes.timelineBody}>{prop.body}</div>
              {prop.footerTitle ? (
                <h6 className={classes.footerTitle}>{prop.footerTitle}</h6>
              ) : null}
              {prop.footer ? <hr className={classes.footerLine} /> : null}
              {prop.footer ? (
                <div className={classes.timelineFooter}>{prop.footer}</div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

InitiativesTimeline.propTypes = {
  stories: PropTypes.arrayOf(PropTypes.object).isRequired,
  simple: PropTypes.bool,
};
